import { Model, Collection } from '@/models/BaseModel';

import {
    equal,
    integer,
    min,
    required,
    string,
    email,
    boolean
} from 'vue-mc/validation';

export class OrderDetail {
    public id: number = 0;
    public product_id: number = 0;
    public quantity: number = 0;
    public product_size: number = 0;
    public product_price: number = 0;
    public product_discount: number = 0;
    public promotion_id: number = 0;
    public category_type: number = 0;
}

export class Order extends Model {
    public options() {
        return {
            identifier: 'id',
            methods: {
                saveOrderItem: 'POST',
                saveDriver: 'POST',
                payByCash: 'POST',
                changeOrderStatus: 'POST',
                changeOrderItemsStatus: 'POST'
            }
        };
    }

    public defaults() {
        return {
            id: null,
        };
    }

    public mutations() {
        return {
            id: (id: any) => Number(id) || null,
            customer_id: Number,
            customer_name: String,
            customer_phone: String,
            table_id: Number,
            table_name: String,
            status: Number,
            comment: String,
            address: String,
            takeout: Boolean,
            delivery: Boolean,
            details: Array<OrderDetail>(),
            created_at: Date,
            driver_id: Number
        };
    }

    public validation() {
        return {
            id: integer.and(min(1)).or(equal(null)),
        };
    }

    public routes() {
        return {
            fetch: "restaurant-orders/{id}",
            saveDriver: "restaurant-orders/{id}/saveDriver",
            saveOrderItem: "restaurant-orders/{id}/saveOrderItem",
            payByCash: "restaurant-orders/{id}/payByCash",
            changeOrderStatus: "restaurant-orders/{id}/changeOrderStatus",
            changeOrderItemsStatus: "restaurant-orders/{id}/changeOrderItemsStatus"
        };
    }

    private orderStatuses = [
        { id: OrderStatus.Ordered, name: "Order received" },
        { id: OrderStatus.ReadyForDelivery, name: "Ready for table/takeout/delivery" },
        { id: OrderStatus.Delivered, name: "Delivered to table" },
        { id: OrderStatus.Paid, name: "In dining paid" },
        { id: OrderStatus.Splitted, name: "Splitted" }
    ];

    public getOrderStatusText() {
        const orderStatus = this.orderStatuses.find(x => x.id === this.status);
        if (orderStatus && orderStatus.id === OrderStatus.Delivered) {
            if (this.takeout) {
                return "Picked up";
            }
            if (this.delivery) {
                return "Out to delivery";
            }
        }

        if (orderStatus && orderStatus.id === OrderStatus.Paid && (this.takeout || this.delivery)) {
            return "Order received";
        }

        return orderStatus ? orderStatus.name : "";
    }

    public saveOrderItem(orderItem: OrderDetail) {
        let method = this.getOption('methods.saveOrderItem');
        let route = this.getRoute('saveOrderItem');
        let params = this.getRouteParameters();
        let url = this.getURL(route, params);
        let data = { id: this.id, details: orderItem };
        let headers = {}
        return this.getRequest({ method, url, data, headers }).send();
    }

    public saveDriver(driverId: number) {
        let method = this.getOption('methods.saveDriver');
        let route = this.getRoute('saveDriver');
        let params = this.getRouteParameters();
        let url = this.getURL(route, params);
        let data = { id: this.id, driver_id: driverId };
        let headers = {}
        return this.getRequest({ method, url, data, headers }).send();
    }

    public payByCash(amount: number) {
        let method = this.getOption('methods.payByCash');
        let route = this.getRoute('payByCash');
        let params = this.getRouteParameters();
        let url = this.getURL(route, params);
        let data = { id: this.id, amount: amount };
        let headers = {}
        return this.getRequest({ method, url, data, headers }).send();
    }

    public changeOrderStatus(status: number) {
        let method = this.getOption('methods.changeOrderStatus');
        let route = this.getRoute('changeOrderStatus');
        let params = this.getRouteParameters();
        let url = this.getURL(route, params);
        let data = { id: this.id, status: status };
        let headers = {}
        return this.getRequest({ method, url, data, headers }).send();
    }

    public changeOrderItemsStatus(status: number, ids: number[]) {
        let method = this.getOption('methods.changeOrderItemsStatus');
        let route = this.getRoute('changeOrderItemsStatus');
        let params = this.getRouteParameters();
        let url = this.getURL(route, params);
        let data = { id: this.id, status: status, items_ids: ids };
        let headers = {}
        return this.getRequest({ method, url, data, headers }).send();
    }
}

export class OrderCollection extends Collection {
    public model() {
        return Order;
    }

    public defaults() {
        return {
            orderBy: 'id',
        };
    }

    public routes() {
        return {
            fetch: "restaurant-orders",
        };
    }

    public fetchData() {
        return this.fetch();
    }
}


export class CurrentOrderCollection extends Collection {
    public model() {
        return Order;
    }

    public defaults() {
        return {
            orderBy: 'id',
        };
    }

    public routes() {
        return {
            fetch: "tables/currentOrders",
        };
    }

    public fetchData() {
        return this.fetch();
    }
}

export enum OrderStatus {
    Ordered = 1,
    ReadyForDelivery = 2,
    Delivered = 3,
    Paid = 4,
    Splitted = 5
}
